<script setup>
import { ref, onMounted, computed, inject, watch, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'

// 注入 API 基础 URL
const apiBaseUrl = inject('apiBaseUrl')
const route = useRoute()

const blog = ref(null)
const isLoading = ref(true)
const error = ref(null)

// 获取博客名称
const blogName = computed(() => route.params.name)

// 加载博客数据
const loadBlog = async () => {
  isLoading.value = true
  error.value = null

  try {
    // 从 blogs.json 获取所有博客
    const response = await fetch(`${apiBaseUrl}/API/readJson.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({ jsonUrl: 'blogs.json' })
    })

    const data = await response.json()
    if (!response.ok) {
      throw new Error(data?.message || `HTTP ${response.status}`)
    }
    console.log(data)
    if (data && data.blogs) {
      // 查找匹配的博客
      const foundBlog = data.blogs.find(b => b.name === blogName.value)

      if (foundBlog) {
        blog.value = foundBlog
      } else {
        error.value = '找不到该博客'
      }
    } else {
      error.value = '无法加载博客数据'
    }
  } catch (err) {
    console.error('加载博客失败:', err)
    error.value = '加载博客时出错'
  } finally {
    isLoading.value = false
  }
}

// 应用博客的 CSS
const applyBlogStyles = () => {
  if (blog.value && blog.value.css) {
    // 创建样式元素
    const styleEl = document.createElement('style')
    styleEl.id = 'blog-custom-styles'
    styleEl.textContent = blog.value.css
    document.head.appendChild(styleEl)
  }
}

// 移除博客的 CSS
const removeBlogStyles = () => {
  const styleEl = document.getElementById('blog-custom-styles')
  if (styleEl) {
    styleEl.remove()
  }
}

onMounted(() => {
  loadBlog()
})

// 监听博客数据变化，应用 CSS
watch(() => blog.value, (newBlog) => {
  removeBlogStyles()
  if (newBlog) {
    applyBlogStyles()
  }
}, { immediate: true })

// 组件卸载时移除样式
onUnmounted(() => {
  removeBlogStyles()
})
</script>

<template>
  <div class="blog-container">
    <div v-if="isLoading" class="loading">
      <div class="spinner"></div>
      <p>加载博客中...</p>
    </div>

    <div v-else-if="error" class="error-container">
      <h2>出错了</h2>
      <p>{{ error }}</p>
      <router-link to="/" class="back-link">返回首页</router-link>
    </div>

    <div v-else-if="blog" class="blog-content">
      <!-- 使用 v-html 渲染博客内容，并添加一个包装器 div -->
      <div class="blog-content-wrapper" v-html="blog.html"></div>
    </div>

    <div v-else class="error-container">
      <h2>博客不存在</h2>
      <router-link to="/" class="back-link">返回首页</router-link>
    </div>
  </div>
</template>

<style scoped>
.blog-container {
  max-width: 1200px;
  margin: 0 auto;
  /* padding: 2rem; */
  min-height: 100vh;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 5px solid rgba(255, 215, 0, 0.3);
  border-radius: 50%;
  border-top-color: #ffd700;
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-container {
  text-align: center;
  padding: 3rem;
  background-color: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  border: 1px dashed rgba(255, 215, 0, 0.3);
  margin-top: 2rem;
}

.error-container h2 {
  color: #ff6b6b;
  margin-bottom: 1rem;
}

.back-link {
  display: inline-block;
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  background-color: rgba(255, 215, 0, 0.2);
  color: #ffd700;
  border: 1px solid rgba(255, 215, 0, 0.5);
  border-radius: 4px;
  text-decoration: none;
  transition: background-color 0.3s ease;
}

.back-link:hover {
  background-color: rgba(255, 215, 0, 0.3);
}

/* 博客内容样式 */
.blog-content {
  color: #e0e0e0;
  line-height: 1.6;
}

.blog-content img {
  max-width: 100% !important;
  height: auto;
  object-fit: cover;
  margin: 1rem 0;
}

/* 添加段落样式 */
.blog-content p {
  margin-bottom: 1rem;
  white-space: pre-wrap;
}

/* 添加标题样式 */
.blog-content h1,
.blog-content h2,
.blog-content h3,
.blog-content h4,
.blog-content h5,
.blog-content h6 {
  margin-top: 1.5rem;
  margin-bottom: 1rem;
}

/* 添加链接样式 */
.blog-content a {
  color: #ffd700;
  text-decoration: underline;
}

.blog-content a:hover {
  color: #ffec8b;
}

/* 注意：博客的自定义 CSS 将通过 JavaScript 动态添加 */
</style>
